/**
 * Tutorial Steps Configuration
 * 
 * Defines all tutorial steps for the interactive guided tour.
 * Each step highlights a specific UI element and provides instructions
 * to help users learn how to use 8bitcomposer.
 * 
 * @module config/tutorialSteps
 */

import type { TutorialStep } from '../types/tutorial';

/**
 * Tutorial Steps Array
 * 
 * Contains all 13 tutorial steps in sequential order.
 * Each step guides the user through a specific feature or concept.
 * 
 * Steps:
 * 1. Welcome - Introduction to the tutorial
 * 2. Grid Basics - How to add notes by clicking cells
 * 3. Note Editing - How to edit note pitch values
 * 4. Keyboard Shortcuts - Using W/S keys for pitch adjustment
 * 5. Instruments - Changing instrument types
 * 6. Volume & Pan - Adjusting channel volume and pan
 * 7. Playback Controls - Play, pause, stop, and loop
 * 8. BPM & Steps - Adjusting tempo and pattern length
 * 9. AI Generation - Using AI to create music
 * 10. Templates - Loading pre-made compositions
 * 11. Export - Saving compositions as WAV files
 * 12. Share - Generating shareable links
 * 13. Completion - Tutorial finished message
 */
export const tutorialSteps: TutorialStep[] = [
  // Step 1: Welcome
  {
    id: 'welcome',
    title: '🎵 Welcome to 8bitcomposer!',
    content: `Welcome to the interactive tutorial! 

This guide will teach you everything you need to know to create amazing 8-bit music.

We'll cover:
• Creating notes on the grid
• Choosing instruments and adjusting sounds
• Using AI to generate music
• Loading templates and exporting your work

Let's get started! Click "Next" to begin.`,
    targetSelector: '#main-content',
    position: 'center',
    allowInteraction: false,
    waitForAction: false,
  },

  // Step 2: Grid Basics
  {
    id: 'grid-basics',
    title: '🎹 The Music Grid',
    content: `This is your composition grid! Each row represents a different instrument channel, and each column is a step in time.

**How to add notes:**
• Click any cell to toggle a note on or off
• Active notes will light up and play when that step is reached
• Try clicking a few cells now to create your first pattern!

The grid plays from left to right, looping continuously.`,
    targetSelector: '#grid-section',
    position: 'top',
    allowInteraction: true,
    waitForAction: false,
    allowClickThrough: true,
  },

  // Step 3: Note Editing
  {
    id: 'note-editing',
    title: '🎼 Editing Note Pitch',
    content: `Each note can have a different pitch! 

**To change a note's pitch:**
• Right-click on any active note (cell with a dot)
• Enter a note value like C4, D#5, F3, or A2
• The number represents the octave (higher = higher pitch)

Try editing a note to hear the difference!

**Tip:** Middle C is C4, which is a good starting point.`,
    targetSelector: '#grid-section',
    position: 'top',
    allowInteraction: true,
    waitForAction: false,
    allowClickThrough: true,
  },

  // Step 4: Keyboard Shortcuts
  {
    id: 'keyboard-shortcuts',
    title: '⌨️ Keyboard Shortcuts',
    content: `Speed up your workflow with keyboard shortcuts!

**Quick pitch adjustment:**
• Hover over any active note
• Press **W** to increase pitch (go up)
• Press **S** to decrease pitch (go down)

This is much faster than right-clicking once you get the hang of it!

**Other shortcuts:**
• Spacebar: Play/Pause
• Delete/Backspace: Clear selected notes`,
    targetSelector: '#grid-section',
    position: 'top',
    allowInteraction: true,
    waitForAction: false,
    allowClickThrough: true,
  },

  // Step 5: Instruments
  {
    id: 'instruments',
    title: '🎸 Choosing Instruments',
    content: `Each row has its own instrument! 8bitcomposer offers classic 8-bit sounds:

**Available instruments:**
• **Square Wave** - Classic chip tune lead sound
• **Sawtooth** - Harsh, buzzy bass sound
• **Triangle** - Smooth, mellow tone
• **Sine** - Pure, soft tone
• **Noise** - Percussion and drum sounds

Try changing an instrument using the dropdown on the left side of any row. Listen to how it changes the character of your notes!`,
    targetSelector: '.row-controls',
    position: 'right',
    allowInteraction: true,
    waitForAction: false,
  },

  // Step 6: Volume & Pan
  {
    id: 'volume-pan',
    title: '🔊 Volume Control',
    content: `Fine-tune your mix with volume controls!

**Volume slider:**
• Drag the slider to adjust the volume for each channel
• Lower volume for background elements
• Higher volume for lead melodies
• You can also mute channels completely with the mute button

**Tip:** A good mix has variety - not everything should be at maximum volume!`,
    targetSelector: '.row-controls',
    position: 'right',
    allowInteraction: true,
    waitForAction: false,
  },

  // Step 7: Playback Controls
  {
    id: 'playback-controls',
    title: '▶️ Playback Controls',
    content: `Control your composition playback:

**Buttons:**
• **Play** ▶️ - Start playback from current position
• **Pause** ⏸️ - Pause playback (resume with Play)
• **Stop** ⏹️ - Stop and return to beginning
• **Loop** 🔁 - Toggle continuous looping

**Keyboard shortcut:** Press Spacebar to play/pause!

Try playing your composition now to hear what you've created.`,
    targetSelector: '#controls-section',
    position: 'bottom',
    allowInteraction: true,
    waitForAction: false,
    allowClickThrough: true,
  },

  // Step 8: BPM & Steps
  {
    id: 'bpm-steps',
    title: '⏱️ Tempo and Length',
    content: `Customize your composition's timing:

**BPM (Beats Per Minute):**
• Controls the speed/tempo of your music
• Lower BPM = slower, more relaxed
• Higher BPM = faster, more energetic
• Try values between 60-180 for most styles

**Steps:**
• Controls the length of your pattern
• More steps = longer, more complex patterns
• Fewer steps = shorter, tighter loops
• Default is 16 steps (one bar)`,
    targetSelector: '#bpm-input',
    position: 'bottom',
    allowInteraction: true,
    waitForAction: false,
    allowClickThrough: true,
  },

  // Step 9: AI Generation
  {
    id: 'ai-generation',
    title: '🤖 AI Music Generation',
    content: `Let AI help you create music instantly!

**How it works:**
• Describe the music you want in the text box
• Click "Generate" to create a composition
• AI will fill the grid with notes matching your description

**Example prompts:**
• "upbeat battle theme with drums"
• "peaceful village music"
• "spooky dungeon ambience"
• "energetic boss fight music"

**Note:** AI generation requires authentication and uses credits. You can also use "Fresh Slate" to clear the grid before generating.`,
    targetSelector: '#generate-section',
    position: 'bottom',
    allowInteraction: false,
    waitForAction: false,
  },

  // Step 10: Templates
  {
    id: 'templates',
    title: '📚 Template Library',
    content: `Start with pre-made compositions!

**Templates are great for:**
• Learning common musical patterns
• Getting inspiration for your own work
• Starting with a solid foundation
• Understanding how different instruments work together

**How to use:**
• Click the "Templates" button to browse
• Choose a template that interests you
• Load it and modify it to make it your own
• Templates can be filtered by category and difficulty

Don't be afraid to experiment - you can always load a new template or start fresh!`,
    targetSelector: '.template-button',
    position: 'bottom',
    allowInteraction: false,
    waitForAction: false,
  },

  // Step 11: Export
  {
    id: 'export',
    title: '💾 Export to WAV',
    content: `Save your composition as an audio file!

**Export feature:**
• Click the "Export WAV" button
• Your composition will be rendered to a WAV file
• The file will download to your computer
• You can then use it in videos, games, or share it

**What gets exported:**
• The entire composition plays through once
• All active channels and their settings
• Current BPM and instrument choices

**Tip:** Make sure your composition sounds good before exporting. Play it through a few times to check!`,
    targetSelector: '#export',
    position: 'top',
    allowInteraction: false,
    waitForAction: false,
  },

  // Step 12: Share
  {
    id: 'share',
    title: '🔗 Share Your Music',
    content: `Share your compositions with others!

**How sharing works:**
• Click the "Share" button
• A unique URL will be generated for your composition
• Anyone with the link can listen to your music
• Shared compositions are view-only (others can't edit)

**Great for:**
• Showing off your creations
• Getting feedback from friends
• Collaborating on ideas
• Building a portfolio

Your composition is saved in the URL itself, so the link will always work!`,
    targetSelector: '#share-btn',
    position: 'top',
    allowInteraction: false,
    waitForAction: false,
  },

  // Step 13: Completion
  {
    id: 'completion',
    title: '🎉 Tutorial Complete!',
    content: `Congratulations! You've learned all the basics of 8bitcomposer!

**You now know how to:**
✓ Create and edit notes on the grid
✓ Choose instruments and adjust volume
✓ Control playback and timing
✓ Use AI to generate music
✓ Load templates for inspiration
✓ Export and share your work

**Next steps:**
• Experiment with different instruments and patterns
• Try creating a full song with multiple sections
• Explore the template library for inspiration
• Share your creations with the community

**Need help?** Click the Help button (?) anytime to review these concepts.

Happy composing! 🎵`,
    targetSelector: '#main-content',
    position: 'center',
    allowInteraction: false,
    waitForAction: false,
  },
];

/**
 * Get tutorial step by ID
 * 
 * @param id - Step ID to find
 * @returns Tutorial step or undefined if not found
 */
export function getTutorialStepById(id: string): TutorialStep | undefined {
  return tutorialSteps.find(step => step.id === id);
}

/**
 * Get tutorial step by index
 * 
 * @param index - Step index (0-based)
 * @returns Tutorial step or undefined if index out of bounds
 */
export function getTutorialStepByIndex(index: number): TutorialStep | undefined {
  return tutorialSteps[index];
}

/**
 * Get total number of tutorial steps
 * 
 * @returns Total number of steps
 */
export function getTutorialStepCount(): number {
  return tutorialSteps.length;
}
